import React from "react";

// **bold** → <strong>, `code` → <code>, [text](url) → <a>
function inline(text) {
  return String(text)
    .split(/(\*\*[^*]+\*\*|`[^`]+`|\[[^\]]+\]\([^)\s]+\))/g)
    .map((part, i) => {
      if (part.startsWith("**") && part.endsWith("**"))
        return <strong key={i}>{part.slice(2, -2)}</strong>;
      if (part.startsWith("`") && part.endsWith("`"))
        return <code key={i}>{part.slice(1, -1)}</code>;
      const link = part.match(/^\[([^\]]+)\]\(([^)\s]+)\)$/);
      if (link)
        return (
          <a key={i} href={link[2]} target="_blank" rel="noreferrer noopener">
            {link[1]}
          </a>
        );
      return part;
    });
}

// A markdown table row "| a | b |" → ["a", "b"]
function splitRow(line) {
  return line
    .replace(/^\s*\|/, "")
    .replace(/\|\s*$/, "")
    .split("|")
    .map((c) => c.trim());
}

const isTableRow = (line) => /^\s*\|.*\|\s*$/.test(line);
const isTableDivider = (line) =>
  /^\s*\|?[\s:|-]+\|[\s:|-]*$/.test(line) && /-/.test(line);

export default function SimpleMarkdown({ md }) {
  const out = [];
  let list = null;

  const flush = () => {
    if (!list) return;
    const Tag = list.type;
    out.push(
      <Tag key={out.length}>
        {list.items.map((t, i) => (
          <li key={i}>{inline(t)}</li>
        ))}
      </Tag>,
    );
    list = null;
  };

  const lines = (md || "").split("\n");

  for (let i = 0; i < lines.length; i++) {
    const raw = lines[i];
    const t = raw.trim();
    let m;

    if (!t) {
      flush();
      continue;
    }

    // ``` fenced code block — kept verbatim
    if ((m = t.match(/^```(.*)$/))) {
      flush();
      const body = [];
      i++;
      while (i < lines.length && !/^\s*```/.test(lines[i])) {
        body.push(lines[i]);
        i++;
      }
      out.push(
        <pre key={out.length}>
          <code>{body.join("\n")}</code>
        </pre>,
      );
      continue;
    }

    // | header | row | followed by a |---|---| divider → real table
    if (isTableRow(raw) && i + 1 < lines.length && isTableDivider(lines[i + 1])) {
      flush();
      const header = splitRow(raw);
      i += 2;
      const rows = [];
      while (i < lines.length && isTableRow(lines[i])) {
        rows.push(splitRow(lines[i]));
        i++;
      }
      i--; // the outer loop advances past the last consumed line
      out.push(
        <table key={out.length}>
          <thead>
            <tr>
              {header.map((c, ci) => (
                <th key={ci}>{inline(c)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, ri) => (
              <tr key={ri}>
                {row.map((c, ci) => (
                  <td key={ci}>{inline(c)}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>,
      );
      continue;
    }

    if ((m = t.match(/^(#{1,6})\s+(.*)/))) {
      flush();
      const Tag = `h${m[1].length}`;
      out.push(<Tag key={out.length}>{inline(m[2])}</Tag>);
      continue;
    }
    if (/^(-{3,}|\*{3,})$/.test(t)) {
      flush();
      out.push(<hr key={out.length} />);
      continue;
    }
    if ((m = t.match(/^>\s?(.*)/))) {
      flush();
      out.push(<blockquote key={out.length}>{inline(m[1])}</blockquote>);
      continue;
    }
    if ((m = t.match(/^[-*]\s+(.*)/))) {
      if (list?.type !== "ul") {
        flush();
        list = { type: "ul", items: [] };
      }
      list.items.push(m[1]);
      continue;
    }
    if ((m = t.match(/^\d+\.\s+(.*)/))) {
      if (list?.type !== "ol") {
        flush();
        list = { type: "ol", items: [] };
      }
      list.items.push(m[1]);
      continue;
    }

    flush();
    out.push(<p key={out.length}>{inline(t)}</p>);
  }

  flush();
  return <>{out}</>;
}
